// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { FC, useEffect, useMemo } from "react";

import { Column, Row, Select, Text } from "@hightouch/htui";
import { capitalize } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { useFormErrorContext } from "src/contexts/form-error-context";
import {
  ConditionType,
  EventCondition,
  FunnelCondition,
  TimestampOperator,
  windowOperatorOptions,
  funnelWindowOperatorOptions,
  shouldResetValue,
  shouldResetTimeType,
  TimestampGranularity,
  TimeType,
} from "src/types/visual";

import { XButton } from "./condition-buttons";
import { validateWindowCondition } from "./condition-validation";
import { Filter } from "./filter";
import { RealtimeEventWindowInput, TimestampInput } from "./timestamp-input";
import { getTimeValue } from "./utils";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import { isDenied } from "src/components/explore/restrictions";
import { UpdaterEnforceDefined } from "src/utils/immutable-helpers";

type Props = {
  isRealtimeEvent?: boolean;
  isDisabled?: boolean;
  eventName?: string;
} & (
  | {
      condition: EventCondition;
      onUpdate: (updater: UpdaterEnforceDefined<EventCondition>) => void;
    }
  | {
      condition: FunnelCondition;
      onUpdate: (updater: UpdaterEnforceDefined<FunnelCondition>) => void;
    }
);

export const WindowFilter: FC<Readonly<Props>> = ({
  isDisabled = false,
  isRealtimeEvent = false,
  condition,
  onUpdate,
  eventName,
}) => {
  const filterId = useMemo<string>(uuidv4, []);
  const { window } = condition;
  const { restrictions } = useQueryBuilderContext();
  const { getErrors, setFieldError, removeErrors } = useFormErrorContext();
  const filterErrors = getErrors(filterId);

  const quantityError = filterErrors?.quantity;
  const valueError = filterErrors?.value;

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    setFieldError(
      filterId,
      validateWindowCondition(window, { isRealtimeEvent }),
    );

    return () => {
      removeErrors([filterId]);
    };
  }, [
    condition.eventModelId,
    setFieldError,
    removeErrors,
    filterId,
    window,
    isRealtimeEvent,
  ]);

  const isFunnelWindow = condition.type === ConditionType.Funnel;
  const operatorLabel = capitalize(
    windowOperatorOptions.find((option) => option.value === window?.operator)
      ?.label,
  );
  const formattedValue = getTimeValue(
    window?.value,
    window?.timeType,
    window?.propertyOptions?.timestampGranularity,
    isFunnelWindow,
  );

  const displayedFilter = (
    <DisplayedFilter
      eventName={eventName}
      formattedValue={formattedValue}
      operator={window?.operator}
      operatorLabel={operatorLabel}
    />
  );

  const isRealtimeEventWindow =
    isRealtimeEvent && window != null && condition.type === ConditionType.Event;

  if (isRealtimeEventWindow) {
    return (
      <Row pl={8}>
        <Filter
          isEmpty={false}
          content={
            <RealtimeEventWindowInput
              window={window}
              // We don't tie `condition` and `onUpdate` together with types
              // so `onUpdate` doesn't get automatically narrowed with `condition`
              onUpdate={
                onUpdate as (
                  updater: UpdaterEnforceDefined<EventCondition>,
                ) => void
              }
            />
          }
          error={quantityError || valueError}
          mr={2}
        >
          {displayedFilter}
        </Filter>
      </Row>
    );
  }

  return (
    <Row pl={8}>
      <Column>
        <Filter
          isEmpty={false} // Field is automatically populated
          content={
            <Row gap={2}>
              <Select
                removePortal
                isDisabled={isDisabled}
                options={
                  isFunnelWindow
                    ? funnelWindowOperatorOptions
                    : windowOperatorOptions
                }
                placeholder="Filter on"
                value={window?.operator}
                width="auto"
                onChange={(operator) => {
                  const propertyOptions = {
                    ...window?.propertyOptions,
                    timestampGranularity: TimestampGranularity.Full,
                  };
                  if (shouldResetTimeType(window?.operator, operator)) {
                    onUpdate((event: EventCondition | FunnelCondition) => {
                      if (event.window) {
                        if (operator) {
                          event.window.operator = operator;
                        }
                        // @ts-expect-error We intentionally clear to show the error state, this is not savable until user fixes
                        event.window.value = undefined;
                        event.window.timeType = TimeType.Relative;
                        event.window.propertyOptions = propertyOptions;
                      }
                    });
                  } else if (
                    shouldResetValue({
                      columnType: null,
                      oldValue: window?.value,
                      newOperator: operator,
                      oldOperator: window?.operator,
                    })
                  ) {
                    onUpdate((event: EventCondition | FunnelCondition) => {
                      if (event.window) {
                        if (operator) {
                          event.window.operator = operator;
                        }
                        // @ts-expect-error We intentionally clear to show the error state, this is not savable until user fixes
                        event.window.value = undefined;
                        event.window.propertyOptions = propertyOptions;
                      }
                    });
                  } else {
                    onUpdate((event: EventCondition | FunnelCondition) => {
                      if (event.window) {
                        if (operator) {
                          event.window.operator = operator;
                        }
                        event.window.propertyOptions = propertyOptions;
                      }
                    });
                  }
                }}
              />
              {window && (
                <TimestampInput
                  isDisabled={isDisabled}
                  condition={window}
                  error={quantityError || valueError}
                  hideTime={false}
                  maxRelativeWindowDays={restrictions?.maxRelativeWindowDays}
                  showRelativeDirections={
                    !(
                      isFunnelWindow ||
                      isDenied(restrictions, "window-directions")
                    )
                  }
                  onUpdate={(updater) =>
                    onUpdate((condition) => {
                      const result = updater(condition.window);
                      if (result) {
                        condition.window = result;
                      }
                    })
                  }
                  allowPropertyComparison={false}
                />
              )}
            </Row>
          }
          error={quantityError || valueError}
          mr={2}
        >
          {displayedFilter}
        </Filter>
      </Column>
      <XButton
        isDisabled={isDisabled}
        onRemove={() =>
          onUpdate((event) => {
            event.window = null;
          })
        }
      />
    </Row>
  );
};

const DisplayedFilter = ({
  eventName,
  formattedValue,
  operator,
  operatorLabel,
}: {
  eventName: string | undefined;
  formattedValue: JSX.Element | "___";
  operator: TimestampOperator | undefined;
  operatorLabel: string;
}) => (
  <Row>
    <Text color="text.secondary" mr={1}>
      {operatorLabel}
    </Text>

    {formattedValue && (
      <>
        {formattedValue}
        {eventName && (
          <>
            <Text fontWeight="medium" ml={1}>
              {operator === TimestampOperator.Between ? "after" : "of"}
            </Text>
            <Text isTruncated ml={1} color="text.secondary" fontWeight="normal">
              {eventName}
            </Text>
          </>
        )}
      </>
    )}
  </Row>
);
